import { useState, useRef, useCallback, useEffect, type UIEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { MarkdownDocument } from '@evi/ui';
import type { TermsStatus } from '@/lib/api';
import styles from './TermsGate.module.css';

/**
 * The terms accept card: read the body (scroll to the end) → tick the
 * checkbox → Accept. Presentational + self-contained scroll/checkbox state,
 * keyed to the doc version so a new version resets the gate. Shared by the
 * blocking TermsGate and the on-demand TermsPromptHost.
 */
export function TermsAcceptCard({
  doc,
  eyebrow,
  dismissLabel,
  isPending,
  isError,
  onAccept,
  onDismiss,
}: {
  doc: TermsStatus;
  eyebrow: string;
  dismissLabel: string;
  isPending: boolean;
  isError: boolean;
  onAccept: () => void;
  onDismiss: () => void;
}) {
  const { t } = useTranslation('chrome');
  const [checked, setChecked] = useState(false);
  const [scrolledEnd, setScrolledEnd] = useState(false);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setChecked(false);
    const el = bodyRef.current;
    // Start every document at the top. The gate now keys this card per
    // document, so this is belt-and-braces for any OTHER caller that reuses
    // one instance across documents.
    if (el) el.scrollTop = 0;
    // A body short enough not to scroll unlocks the checkbox immediately.
    setScrolledEnd(el ? el.scrollHeight - el.clientHeight <= 4 : !doc.body);
  }, [doc.doc_type, doc.version, doc.body]);

  const onScroll = useCallback((e: UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) setScrolledEnd(true);
  }, []);

  const canAccept = checked && scrolledEnd && !isPending;

  return (
    <div className={styles.card}>
      <p className={styles.eyebrow}>{eyebrow}</p>
      <h2 className={styles.title}>{doc.label}</h2>
      <p className={styles.version}>{t('terms.version', { version: doc.version })}</p>

      {doc.body ? (
        <div
          className={styles.body}
          ref={bodyRef}
          onScroll={onScroll}
          tabIndex={0}
          role="region"
          aria-label={doc.label}
        >
          {/* Rendered markdown, not a raw string. These bodies carry ##
              headings, **bold**, bullet lists and the Sales Partner
              commission-tier TABLE. Printed as text through the old
              white-space: pre-wrap, a Partner would read the rates governing
              their money as `|---|---|` pipes. */}
          <MarkdownDocument text={doc.body} />
        </div>
      ) : doc.hosted_url ? (
        <div className={styles.hosted}>
          <a href={doc.hosted_url} target="_blank" rel="noopener noreferrer">
            {t('terms.readFull', { label: doc.label })}
          </a>
        </div>
      ) : null}

      {doc.body && !scrolledEnd && (
        <p className={styles.scrollHint}>{t('terms.scrollHint')}</p>
      )}

      <label className={styles.checkboxRow}>
        <input
          type="checkbox"
          checked={checked}
          disabled={!scrolledEnd && Boolean(doc.body)}
          onChange={(e) => setChecked(e.target.checked)}
        />
        <span>{t('terms.agree', { label: doc.label })}</span>
      </label>

      {isError && (
        <div className={styles.error} role="alert">
          {t('terms.error')}
        </div>
      )}

      <div className={styles.actions}>
        <button type="button" className={styles.ghost} onClick={onDismiss} disabled={isPending}>
          {dismissLabel}
        </button>
        <button type="button" className={styles.primary} onClick={onAccept} disabled={!canAccept}>
          {isPending ? <><Loader2 size={14} className={styles.spin} /> {t('terms.saving')}</> : t('terms.accept')}
        </button>
      </div>
    </div>
  );
}
